import { useEffect, useRef, useState } from 'react';

/**
 * Renders one PDF page onto a canvas at a fixed CSS width and overlays
 * `children` (field boxes / inputs) in an absolutely-positioned layer that
 * exactly covers the page — the layer is the coordinate space the percentage
 * field positions map onto.
 */
export default function PdfPage({ pdf, pageNumber, width = 760, children, onPointerDownCapture }) {
  const canvasRef = useRef(null);
  const [height, setHeight] = useState(Math.round(width * 1.294)); // letter-ish placeholder

  useEffect(() => {
    let cancelled = false;
    let renderTask = null;
    (async () => {
      const page = await pdf.getPage(pageNumber);
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      const scale = width / base.width;
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      setHeight(Math.round(viewport.height));
      const ctx = canvas.getContext('2d');
      renderTask = page.render({
        canvasContext: ctx,
        viewport,
        transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
      });
      await renderTask.promise.catch(() => undefined);
    })();
    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdf, pageNumber, width]);

  return (
    <div
      data-page-number={pageNumber}
      className="relative mx-auto bg-white shadow-md"
      style={{ width, height }}
      onPointerDownCapture={onPointerDownCapture}
    >
      <canvas ref={canvasRef} className="block" />
      <div className="absolute inset-0">{children}</div>
    </div>
  );
}
